import React, { useState, useRef } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import logo from "./logo.png";
import "./AdmissionForm.css";

const LANGUAGES = [
  "English",
  "French",
  "Spanish",
  "German",
  "Chinese (Mandarin)",
  "Arabic",
  "Japanese",
  "Korean",
  "Turkish",
  "Italian",
];

const emptyForm = {
  name: "",
  fatherName: "",
  gender: "",
  level: "",
  contact: "",
  email: "",
  address: "",
  language: "",
  cnic: "",
  slot: "Morning",
};

// Pakistani CNIC auto-format: 12345-1234567-1
function formatCNIC(value) {
  const digits = value.replace(/\D/g, "").slice(0, 13);
  const p1 = digits.slice(0, 5);
  const p2 = digits.slice(5, 12);
  const p3 = digits.slice(12, 13);
  let out = p1;
  if (p2) out += "-" + p2;
  if (p3) out += "-" + p3;
  return out;
}

function todayFormatted() {
  const d = new Date();
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function AdmissionForm() {
  const [form, setForm] = useState(emptyForm);
  const [submitted, setSubmitted] = useState(null);
  const [errors, setErrors] = useState({});
  const previewRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "cnic") {
      setForm((f) => ({ ...f, cnic: formatCNIC(value) }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Student name is required";
    if (!form.fatherName.trim()) err.fatherName = "Father name is required";
    if (!form.gender) err.gender = "Please select gender";
    if (!form.level.trim()) err.level = "Level is required";
    if (!form.contact.trim()) err.contact = "Contact number is required";
    else if (!/^[0-9+\-\s]{7,15}$/.test(form.contact.trim()))
      err.contact = "Enter a valid contact number";
    if (!form.email.trim()) err.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      err.email = "Enter a valid email address";
    if (!form.address.trim()) err.address = "Address is required";
    if (!form.language) err.language = "Please select a language";
    if (!form.cnic.trim()) err.cnic = "CNIC is required";
    else if (form.cnic.replace(/\D/g, "").length !== 13)
      err.cnic = "CNIC must be 13 digits";
    return err;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    setErrors(err);
    if (Object.keys(err).length === 0) {
      setSubmitted({ ...form, date: todayFormatted() });
    }
  };

  const handleEdit = () => setSubmitted(null);

  const handleReset = () => {
    setForm(emptyForm);
    setErrors({});
    setSubmitted(null);
  };

  const handleDownloadPDF = async () => {
    const node = previewRef.current;
    if (!node) return;

    const canvas = await html2canvas(node, {
      scale: 3,
      backgroundColor: "#f7f3e8",
      useCORS: true,
    });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pageWidth - 24;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const x = (pageWidth - imgWidth) / 2;
    const y = Math.min(15, (pageHeight - imgHeight) / 2);

    pdf.addImage(imgData, "PNG", x, y, imgWidth, imgHeight);
    pdf.save(`${submitted.name.replace(/\s+/g, "_")}_admission_form.pdf`);
  };

  return (
    <div className="adm-page">
      <div className="adm-wrap">
        <header className="adm-header">
          <img
            src={logo}
            alt="Institute of Applied Languages"
            className="adm-logo"
            style={{ width: 78, height: 78, objectFit: "cover", borderRadius: "50%" }}
          />
          <span className="adm-eyebrow">Institute Of Applied Languages</span>
          <h1 className="adm-title">Student Admission Form</h1>
          <p className="adm-subtitle">
            Kindly fill in all details accurately for registration.
          </p>
        </header>

        {!submitted ? (
          <form className="adm-card" onSubmit={handleSubmit} noValidate>
            <div className="adm-grid">
              <div className="adm-field">
                <label htmlFor="name">Student Name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="e.g. Ayesha Khan"
                  value={form.name}
                  onChange={handleChange}
                />
                {errors.name && <span className="adm-error">{errors.name}</span>}
              </div>

              <div className="adm-field">
                <label htmlFor="fatherName">Father Name</label>
                <input
                  id="fatherName"
                  name="fatherName"
                  type="text"
                  placeholder="e.g. Imran Khan"
                  value={form.fatherName}
                  onChange={handleChange}
                />
                {errors.fatherName && (
                  <span className="adm-error">{errors.fatherName}</span>
                )}
              </div>
            </div>

            <div className="adm-grid">
              <div className="adm-field">
                <label>Gender</label>
                <div className="adm-toggle-group">
                  {["Male", "Female", "Other"].map((g) => (
                    <label
                      key={g}
                      className={`adm-toggle-option ${form.gender === g ? "active" : ""}`}
                    >
                      <input
                        type="radio"
                        name="gender"
                        value={g}
                        checked={form.gender === g}
                        onChange={handleChange}
                      />
                      {g}
                    </label>
                  ))}
                </div>
                {errors.gender && <span className="adm-error">{errors.gender}</span>}
              </div>

              <div className="adm-field">
                <label htmlFor="level">Level</label>
                <input
                  id="level"
                  name="level"
                  type="text"
                  placeholder="e.g. Beginner"
                  value={form.level}
                  onChange={handleChange}
                />
                {errors.level && <span className="adm-error">{errors.level}</span>}
              </div>
            </div>

            <div className="adm-grid">
              <div className="adm-field">
                <label htmlFor="contact">Contact Number</label>
                <input
                  id="contact"
                  name="contact"
                  type="text"
                  placeholder="e.g. 0300-1234567"
                  value={form.contact}
                  onChange={handleChange}
                />
                {errors.contact && (
                  <span className="adm-error">{errors.contact}</span>
                )}
              </div>

              <div className="adm-field">
                <label htmlFor="email">Email Address</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="e.g. ayesha@email.com"
                  value={form.email}
                  onChange={handleChange}
                />
                {errors.email && <span className="adm-error">{errors.email}</span>}
              </div>
            </div>

            <div className="adm-field">
              <label htmlFor="address">Address</label>
              <textarea
                id="address"
                name="address"
                rows={2}
                placeholder="House #, Street, City"
                value={form.address}
                onChange={handleChange}
              />
              {errors.address && <span className="adm-error">{errors.address}</span>}
            </div>

            <div className="adm-grid">
              <div className="adm-field">
                <label htmlFor="language">Language to Learn</label>
                <select
                  id="language"
                  name="language"
                  value={form.language}
                  onChange={handleChange}
                >
                  <option value="">Select a language</option>
                  {LANGUAGES.map((lang) => (
                    <option key={lang} value={lang}>
                      {lang}
                    </option>
                  ))}
                </select>
                {errors.language && (
                  <span className="adm-error">{errors.language}</span>
                )}
              </div>

              <div className="adm-field">
                <label htmlFor="cnic">CNIC</label>
                <input
                  id="cnic"
                  name="cnic"
                  type="text"
                  placeholder="12345-1234567-1"
                  value={form.cnic}
                  onChange={handleChange}
                  inputMode="numeric"
                />
                {errors.cnic && <span className="adm-error">{errors.cnic}</span>}
              </div>
            </div>

            <div className="adm-field">
              <label>Preferred Slot</label>
              <div className="adm-toggle-group">
                {["Morning", "Evening"].map((s) => (
                  <label
                    key={s}
                    className={`adm-toggle-option ${form.slot === s ? "active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="slot"
                      value={s}
                      checked={form.slot === s}
                      onChange={handleChange}
                    />
                    {s}
                  </label>
                ))}
              </div>
            </div>

            <div className="adm-actions">
              <button type="button" className="adm-btn adm-btn-ghost" onClick={handleReset}>
                Clear
              </button>
              <button type="submit" className="adm-btn adm-btn-primary">
                Preview Admission
              </button>
            </div>
          </form>
        ) : (
          <div className="adm-preview-wrap">
            <div className="adm-ticket" ref={previewRef}>
              <div className="adm-ticket-top">
                <img
                  src={logo}
                  alt="Institute of Applied Languages"
                  className="adm-ticket-logo"
                  style={{ width: 56, height: 56, objectFit: "cover", borderRadius: "50%" }}
                />
                <div className="adm-ticket-heading">
                  <span className="adm-eyebrow adm-eyebrow-dark">
                    Institute Of Applied Languages
                  </span>
                  <h2 className="adm-ticket-title">Admission Confirmation</h2>
                </div>
                <div className="adm-ticket-no">
                  <span>{submitted.date}</span>
                </div>
              </div>

              <div className="adm-ticket-divider">
                {Array.from({ length: 44 }).map((_, i) => (
                  <span key={i} />
                ))}
              </div>

              <div className="adm-ticket-body">
                <div className="adm-ticket-row">
                  <span className="adm-ticket-label">Student Name</span>
                  <span className="adm-ticket-value">{submitted.name}</span>
                </div>
                <div className="adm-ticket-row">
                  <span className="adm-ticket-label">Father Name</span>
                  <span className="adm-ticket-value">{submitted.fatherName}</span>
                </div>
                <div className="adm-ticket-row">
                  <span className="adm-ticket-label">Gender</span>
                  <span className="adm-ticket-value">{submitted.gender}</span>
                </div>
                <div className="adm-ticket-row">
                  <span className="adm-ticket-label">Level</span>
                  <span className="adm-ticket-value">{submitted.level}</span>
                </div>
                <div className="adm-ticket-row">
                  <span className="adm-ticket-label">Contact</span>
                  <span className="adm-ticket-value">{submitted.contact}</span>
                </div>
                <div className="adm-ticket-row">
                  <span className="adm-ticket-label">Email</span>
                  <span className="adm-ticket-value">{submitted.email}</span>
                </div>
                <div className="adm-ticket-row">
                  <span className="adm-ticket-label">Address</span>
                  <span className="adm-ticket-value">{submitted.address}</span>
                </div>
                <div className="adm-ticket-row">
                  <span className="adm-ticket-label">CNIC</span>
                  <span className="adm-ticket-value">{submitted.cnic}</span>
                </div>
                <div className="adm-ticket-row">
                  <span className="adm-ticket-label">Language</span>
                  <span className="adm-ticket-value">{submitted.language}</span>
                </div>
                <div className="adm-ticket-row">
                  <span className="adm-ticket-label">Slot</span>
                  <span className="adm-ticket-value">{submitted.slot}</span>
                </div>
                <div className="adm-ticket-row">
                  <span className="adm-ticket-label">Date Issued</span>
                  <span className="adm-ticket-value">{submitted.date}</span>
                </div>
              </div>

              <div className="adm-ticket-footer">
                <span>Please retain this admission slip for your records.</span>
              </div>
            </div>

            <div className="adm-actions adm-actions-preview">
              <button className="adm-btn adm-btn-ghost" onClick={handleEdit}>
                Edit Details
              </button>
              <button className="adm-btn adm-btn-primary" onClick={handleDownloadPDF}>
                Download PDF
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}